"use client";
import React from "react";
import { motion } from "framer-motion";
import { getAssetUrl } from "../services/api";
import { getIcon } from "../utils/iconHelper";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const getIconColorClass = (iconName) => {
  const colors = {
    FaUnity: "text-indigo-500 dark:text-indigo-400",
    MdAnimation: "text-pink-500 dark:text-pink-400",
    FaGithub: "text-gray-600 dark:text-gray-300",
    GiLightningFrequency: "text-yellow-500 dark:text-yellow-400",
    SiFirebase: "text-orange-500 dark:text-orange-400",
    FaCode: "text-green-600 dark:text-green-400",
    SiDotnet: "text-purple-500 dark:text-purple-400"
  };
  return colors[iconName] || "text-indigo-500 dark:text-indigo-400";
};

export default function About({ aboutData, highlightsData }) {
  const data = aboutData || {
    heading: "About Me",
    description: "Hi, I’m Abdul Haseeb, a Game Developer and Full-Stack Engineer. I specialize in creating immersive 2D & 3D games using Unity and DOTween, while also building scalable web & mobile apps with modern stacks. My expertise lies in combining performance optimization, clean coding to deliver engaging digital experiences 🚀.",
    about_image: "/about.png"
  };

  const highlightsList = highlightsData || [
    { icon_name: "FaUnity", text: "Unity (2D & 3D Game Dev)" },
    { icon_name: "MdAnimation", text: "DOTween (Animations & UI)" },
    { icon_name: "FaGithub", text: "GitHub (Version Control)" },
    { icon_name: "GiLightningFrequency", text: "Optimization (Mobile/PC)" },
    { icon_name: "SiFirebase", text: "Firebase (Cloud & Auth)" },
    { icon_name: "FaCode", text: "General Coding & Algorithms" }
  ];

  const highlights = highlightsList.map(hl => ({
    icon: (
      <span className="flex items-center justify-center w-5 h-5">
        {getIcon(hl.icon_name, 22, getIconColorClass(hl.icon_name))}
      </span>
    ),
    text: hl.text
  }));

  return (
    <section
      id="about"
      className="min-h-screen flex flex-col md:flex-row items-center justify-center 
      bg-gray-50 dark:bg-gradient-to-b dark:from-black dark:via-gray-900 dark:to-black 
      text-gray-900 dark:text-white px-8 py-20 transition-colors duration-500"
    >
      {/* Left Image */}
      <motion.div
        className="flex-1 flex justify-center mb-10 md:mb-0"
        initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1, type: "spring" }}
        viewport={{ once: true }}
      >
        <img
          src={getAssetUrl(data.about_image)}
          alt="About Me"
          className="w-72 h-72 md:w-96 md:h-96 object-cover rounded-2xl 
          shadow-[0_0_40px_rgba(139,92,246,0.6)]"
        />
      </motion.div>

      {/* Right Content */}
      <motion.div
        className="flex-1 space-y-6 text-center md:text-left"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Heading */}
        <motion.h2
          className="text-5xl font-extrabold tracking-wider font-[Cinzel]"
          variants={item}
        >
          {data.heading.split(" ")[0]}{" "}
          <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            {data.heading.split(" ").slice(1).join(" ")}
          </span>
        </motion.h2>

        {/* Description */}
        <motion.p
          className="text-gray-700 dark:text-gray-300 leading-relaxed max-w-xl font-[Inter] text-lg"
          variants={item}
        >
          {data.description}
        </motion.p>

        {/* Highlights with Icons */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8"
          variants={container}
        >
          {highlights.map((skill, i) => (
            <motion.span
              key={i}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/5 text-sm rounded-lg text-gray-700 dark:text-gray-300 
              border border-gray-300 dark:border-gray-700 font-[Poppins]
              hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 
              hover:text-white transition-all duration-300 shadow-md"
              variants={item}
              whileHover={{ scale: 1.05 }}
            >
              {skill.icon} {skill.text}
            </motion.span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
